import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, MapPin } from 'lucide-react';
import tournamentService, { Tournament } from '../../services/tournamentService';
import { t as tr } from '../../i18n';

const ACCENT = '#3fced1';
const MAX_ITEMS = 5;
const MONTHS = [tr('web.service.news-service.jan'), tr('web.service.news-service.feb'), tr('web.service.news-service.maer'), tr('web.service.news-service.apr'), tr('web.service.news-service.mai'), tr('web.service.news-service.jun'), tr('web.service.news-service.jul'), tr('web.service.news-service.aug'), tr('web.service.news-service.sep'), tr('web.service.news-service.okt'), tr('web.service.news-service.nov'), tr('web.service.news-service.dez')];

interface UpcomingItem {
  tournament: Tournament;
  nextTs: number;
}

/**
 * Sidebar-Widget "Kommende Turniere": die nächsten Community-Turniere als
 * kompakte Zeilen (Datum-Badge + Name + Ort). Blendet sich ohne Daten aus.
 */
const SidebarUpcoming: React.FC = () => {
  const [items, setItems] = useState<UpcomingItem[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    tournamentService
      .getTournaments({ limit: 60, sortBy: 'date', sortOrder: 'asc' })
      .then((res) => {
        if (cancelled) return;
        const now = new Date();
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
        const upcoming: UpcomingItem[] = (res.data || [])
          .filter((t) => t && t.slug && t.cancelled !== true)
          .map((t) => ({ tournament: t, nextTs: tournamentService.getNextTimestamp(t) ?? 0 }))
          .filter((it) => it.nextTs >= todayStart)
          .sort((a, b) => a.nextTs - b.nextTs)
          .slice(0, MAX_ITEMS);
        setItems(upcoming);
      })
      .catch(() => {
        /* Widget bleibt einfach ausgeblendet */
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // CLS-Fix (2026-09-01): Skeleton in Endhöhe (Header + 5 Zeilen + Footer)
  // statt null, solange die Turniere laden
  if (!loaded) {
    return (
      <section
        className="overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900"
        aria-hidden="true"
      >
        <div className="border-b border-zinc-800 px-4 py-3">
          <h2 className="section-heading text-base font-bold text-white">{tr('web.component.home-upcoming-tournaments.kommende-turniere')}</h2>
        </div>
        <div>
          {Array.from({ length: MAX_ITEMS }).map((_, i) => (
            <div
              key={i}
              className="flex items-center gap-3 border-b border-zinc-800 px-4 py-2.5 last:border-b-0"
            >
              <div className="h-10 w-10 shrink-0 animate-pulse rounded-lg bg-zinc-800" />
              <div className="min-w-0 flex-1 space-y-2">
                <div className="h-3.5 w-4/5 animate-pulse rounded bg-zinc-800" />
                <div className="h-3 w-2/5 animate-pulse rounded bg-zinc-800" />
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-center border-t border-zinc-800 px-4 py-2.5">
          <div className="h-5 w-28 animate-pulse rounded bg-zinc-800" />
        </div>
      </section>
    );
  }

  if (items.length === 0) return null;

  return (
    <section className="overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900">
      <div className="border-b border-zinc-800 px-4 py-3">
        <h2 className="section-heading text-base font-bold text-white">{tr('web.component.home-upcoming-tournaments.kommende-turniere')}</h2>
      </div>
      <div>
        {items.map(({ tournament, nextTs }) => {
          const d = new Date(nextTs);
          const city = tournament.address?.city || tournament.address?.label || null;
          return (
            <Link
              key={tournament._id || tournament.id || tournament.slug}
              to={`/turnier/${tournament.slug}`}
              className="flex items-center gap-3 border-b border-zinc-800 px-4 py-2.5 transition-colors last:border-b-0 hover:bg-zinc-800/40"
            >
              <div className="flex h-10 w-10 shrink-0 flex-col items-center justify-center rounded-lg bg-zinc-800">
                <span className="text-sm font-bold leading-none text-white">{d.getDate()}</span>
                <span className="mt-0.5 text-[9px] font-semibold uppercase leading-none text-gray-500">
                  {MONTHS[d.getMonth()]}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="truncate text-sm font-semibold text-white">
                  {tournament.name || tr('web.component.create-entry-modal.turnier')}
                </h3>
                {city && (
                  <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-gray-500">
                    <MapPin size={11} className="shrink-0" />
                    <span className="truncate">{city}</span>
                  </p>
                )}
              </div>
            </Link>
          );
        })}
      </div>
      <Link
        to="/turniere"
        className="flex items-center justify-center gap-1 border-t border-zinc-800 px-4 py-2.5 text-sm font-semibold transition-colors hover:bg-zinc-800/40"
        style={{ color: ACCENT }}
      >
        {tr('web.component.home-pdc-calendar.alle-turniere')}{' '}<ChevronRight size={14} />
      </Link>
    </section>
  );
};

export default SidebarUpcoming;
